import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Chip, Table, TableHead, TableBody, TableRow, TableCell, Stack } from '@mui/material';
import { fmtC } from '../../lib/utils';
import { useMobile } from '../../hooks/useMobile';

export default function CreateJobConfirmDialog({ state, computed, onConfirm, onCancel }) {
  const { isMobile } = useMobile();
  const typeBadge = state.mode === 're' ? 'Real Estate' : state.mode === 'virtour' ? 'VirTour' : 'Custom';

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Chip label={typeBadge} size="small" color="primary" variant="outlined" sx={{ textTransform: 'uppercase', fontWeight: 700, fontSize: '0.65rem' }} />
        <Typography variant="h6" component="span">Create Job?</Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {/* Summary grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', mb: 2, fontSize: '0.875rem' }}>
          <Typography variant="body2" color="text.secondary">Client</Typography>
          <Typography variant="body2" fontWeight={600}>{state.client || '—'}</Typography>
          <Typography variant="body2" color="text.secondary">Quote #</Typography>
          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{state.quoteNumber}</Typography>
          {(state.mode === 're' || state.mode === 'virtour') && state.address && (
            <>
              <Typography variant="body2" color="text.secondary">Address</Typography>
              <Typography variant="body2">{state.address}</Typography>
            </>
          )}
          {state.originator && (
            <>
              <Typography variant="body2" color="text.secondary">Originator</Typography>
              <Typography variant="body2">{state.originator}</Typography>
            </>
          )}
        </Box>

        {/* Line items */}
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 240, overflowY: 'auto', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Service</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Shoot</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Delivery</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.lines.map((line) => (
                <TableRow key={line.id}>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{line.desc || '(untitled)'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {(line.schedule && line.schedule.length > 0)
                      ? line.schedule.map((s, i) => (
                          <div key={i}>
                            {s.date || '—'}
                            {s.endDate ? ` → ${s.endDate}` : ''}
                            {s.time ? ` ${s.time}` : ''}
                          </div>
                        ))
                      : (line.shootDate || '—') + (line.shootTime ? ` ${line.shootTime}` : '')
                    }
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {line.deliveryDate || '—'}{line.deliveryTime ? ` ${line.deliveryTime}` : ''}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{fmtC(line.qty * line.rate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Discount + Total */}
        <Stack direction="row" justifyContent="flex-end" spacing={3} sx={{ mt: 2 }}>
          {computed.discountAmount > 0 && (
            <Typography variant="body2" color="success.main">Discount: -{fmtC(computed.discountAmount)}</Typography>
          )}
          <Typography variant="h6" color="primary" fontWeight={700}>Total: {fmtC(computed.total)}</Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button size="small" onClick={onCancel}>Cancel</Button>
        <Button size="small" variant="contained" onClick={onConfirm}>Confirm &amp; Create Job</Button>
      </DialogActions>
    </Dialog>
  );
}
